// SPDX-License-Identifier: AGPL-3.0-or-later

import Authentication from '@app/features/auth/state/Authentication';
import type {Channel} from '@app/features/channel/models/Channel';
import * as TableGameCommands from '@app/features/table_game/commands/TableGameCommands';
import styles from '@app/features/table_game/components/TableGameResult.module.css';
import {UPDATE_APP_DESCRIPTOR} from '@app/features/table_game/utils/TableGameMessages';
import {LeagueBadge} from '@app/features/table_game_leaderboard/components/LeagueBadge';
import TableGameRankedResult from '@app/features/table_game_leaderboard/state/TableGameRankedResult';
import {
	LEAGUE_PROGRESS_DESCRIPTOR,
	PLACEMENT_LEFT_DESCRIPTOR,
	RR_DESCRIPTOR,
	TABLE_GAME_LEAGUE_DESCRIPTORS,
} from '@app/features/table_game_leaderboard/utils/TableGameLeaderboardMessages';
import {WarningAlert} from '@app/features/ui/warning_alert/WarningAlert';
import Users from '@app/features/user/state/Users';
import * as NicknameUtils from '@app/features/user/utils/NicknameUtils';
import {
	isKnownTableGameKind,
	isTableGameRankedKind,
	type TableGameRankedKind,
} from '@fluxer/constants/src/TableGameConstants';
import type {TableGameRatingSummary} from '@fluxer/schema/src/domains/table_game/TableGameLeaderboardSchemas';
import type {
	TableGameResult as TableGameResultValue,
	TableGameSummary,
} from '@fluxer/schema/src/domains/table_game/TableGameSchemas';
import type {I18n, MessageDescriptor} from '@lingui/core';
import {msg} from '@lingui/core/macro';
import {useLingui} from '@lingui/react/macro';
import {observer} from 'mobx-react-lite';
import {useEffect} from 'react';

const WON_DESCRIPTOR = msg({
	message: '{names} won',
	comment: 'Table games: result. {names} is one or more player names.',
});
const DRAW_DESCRIPTOR = msg({message: 'Draw', comment: 'Table games: the game ended without a winner.'});
const CANCELED_DESCRIPTOR = msg({
	message: 'The table was closed',
	comment: 'Table games: the table ended without a result.',
});
const LEFT_DESCRIPTOR = msg({
	message: '{name} left the table and lost',
	comment: 'Table games: result when a player left on purpose. {name} is the player.',
});
const DISCONNECTED_DESCRIPTOR = msg({
	message: '{name} did not come back in time and lost',
	comment: 'Table games: result when a player lost the connection for over 30 seconds. {name} is the player.',
});
const TIMEOUTS_DESCRIPTOR = msg({
	message: '{name} missed two turns in a row and lost',
	comment: 'Table games: result when a player ran out of time twice in a row. {name} is the player.',
});

const DETAIL_DESCRIPTORS: Partial<Record<string, MessageDescriptor>> = {
	checkmate: msg({message: 'Checkmate', comment: 'Table games: chess result detail.'}),
	timeout: msg({
		message: 'On time',
		comment: 'Table games: chess or draughts result detail, the other side ran out of time.',
	}),
	resign: msg({message: 'By resignation', comment: 'Table games: chess result detail.'}),
	pairs: msg({message: 'Finished with pairs', comment: 'Table games: Okey result detail.'}),
	okey: msg({message: 'Finished with the okey', comment: 'Table games: Okey result detail.'}),
	hand: msg({message: 'Finished in one go', comment: 'Table games: 101 Okey, finished without opening earlier.'}),
	score: msg({message: 'Lowest score', comment: 'Table games: 101 Okey result detail.'}),
	pile_empty: msg({message: 'The pile ran out', comment: 'Table games: Okey draw detail.'}),
	stalemate: msg({message: 'Stalemate', comment: 'Table games: chess draw detail.'}),
	insufficient: msg({message: 'Not enough pieces to mate', comment: 'Table games: chess draw detail.'}),
	threefold: msg({message: 'Same position three times', comment: 'Table games: chess draw detail.'}),
	fifty_move: msg({message: 'Fifty moves without a capture', comment: 'Table games: chess draw detail.'}),
	bear_off: msg({message: 'All checkers borne off', comment: 'Table games: backgammon result detail.'}),
	mars: msg({
		message: 'Gammon',
		comment: 'Table games: backgammon, the winner finished before the loser bore off any checker (Mars).',
	}),
	no_pieces: msg({message: 'No pieces left', comment: 'Table games: draughts result detail, every piece was taken.'}),
	blocked: msg({message: 'No moves left', comment: 'Table games: draughts result detail, the loser could not move.'}),
	move_limit: msg({
		message: '25 moves without progress',
		comment: 'Table games: draughts draw detail, 25 moves each without a capture or a man moving forward.',
	}),
	lone_kings: msg({message: 'One king each', comment: 'Table games: Turkish draughts draw detail.'}),
	disabled: msg({message: 'Table games were turned off', comment: 'Table games: why a table closed.'}),
	theme_changed: msg({message: 'The room changed', comment: 'Table games: why a table closed.'}),
	abandoned: msg({message: 'Nobody started the game', comment: 'Table games: why a table closed.'}),
};

// The server records a ranked game a few seconds after it ends; my rating is read again then.
const RANKED_READ_DELAYS_MS = [3_000, 10_000];

function rankedChangeText(i18n: I18n, before: TableGameRatingSummary | null, after: TableGameRatingSummary): string {
	if (!after.league) return i18n._(PLACEMENT_LEFT_DESCRIPTOR, {count: after.placement_left});
	const league = i18n._(TABLE_GAME_LEAGUE_DESCRIPTORS[after.league]);
	if (before?.league && before.league !== after.league) {
		return `${i18n._(TABLE_GAME_LEAGUE_DESCRIPTORS[before.league])} → ${league}`;
	}
	if (after.rr !== null) {
		const delta = before?.rr != null ? after.rr - before.rr : null;
		const rr = i18n._(RR_DESCRIPTOR, {rr: after.rr});
		return delta === null ? `${league} · ${rr}` : `${league} · ${rr} (${delta >= 0 ? '+' : ''}${delta})`;
	}
	const percent = (progress: number) => i18n._(LEAGUE_PROGRESS_DESCRIPTOR, {percent: Math.floor(progress * 100)});
	if (after.progress === null) return league;
	return before?.progress != null && before.league === after.league
		? `${league} · ${percent(before.progress)} → ${percent(after.progress)}`
		: `${league} · ${percent(after.progress)}`;
}

const RankedChange = observer(function RankedChange({
	channelId,
	slot,
	game,
}: {
	channelId: string;
	slot: string;
	game: TableGameRankedKind;
}) {
	const {i18n} = useLingui();
	useEffect(() => {
		const timers = RANKED_READ_DELAYS_MS.map((delay) =>
			setTimeout(() => void TableGameCommands.loadRankedAfter(channelId, slot, game), delay),
		);
		return () => {
			for (const timer of timers) clearTimeout(timer);
		};
	}, [channelId, slot, game]);
	const result = TableGameRankedResult.result;
	if (!result || result.tableKey !== `${channelId}:${slot}` || !result.after) return null;
	return (
		<span className={styles.ranked} data-flx="table-game.table-game-result.ranked">
			{result.after.league && (
				<LeagueBadge league={result.after.league} data-flx="table-game.table-game-result.league-badge" />
			)}
			<span>{rankedChangeText(i18n, result.before, result.after)}</span>
		</span>
	);
});

function detailKey(result: TableGameResultValue): string {
	if (result.kind === 'win') return result.finish && result.finish !== 'normal' ? result.finish : result.reason;
	return result.reason;
}

export const TableGameResult = observer(function TableGameResult({
	channel,
	summary,
}: {
	channel: Channel;
	summary: TableGameSummary;
}) {
	const {i18n} = useLingui();
	const result = summary.result;
	if (!result) return null;
	if (!isKnownTableGameKind(summary.game)) {
		return (
			<WarningAlert data-flx="table-game.table-game-result.update-notice">{i18n._(UPDATE_APP_DESCRIPTOR)}</WarningAlert>
		);
	}
	const nameAt = (position: number) => {
		const userId = summary.players.find((player) => player.position === position)?.user_id;
		const user = userId ? Users.getUser(userId) : undefined;
		return user ? NicknameUtils.getNickname(user, channel.guildId) : '';
	};
	let title: string;
	switch (result.kind) {
		case 'win':
			title = i18n._(WON_DESCRIPTOR, {names: result.winners.map(nameAt).join(', ')});
			break;
		case 'forfeit': {
			const descriptor =
				result.reason === 'leave'
					? LEFT_DESCRIPTOR
					: result.reason === 'disconnect'
						? DISCONNECTED_DESCRIPTOR
						: TIMEOUTS_DESCRIPTOR;
			title = i18n._(descriptor, {name: nameAt(result.loser)});
			break;
		}
		case 'draw':
			title = i18n._(DRAW_DESCRIPTOR);
			break;
		case 'canceled':
			title = i18n._(CANCELED_DESCRIPTOR);
			break;
	}
	const detail = result.kind === 'forfeit' ? undefined : DETAIL_DESCRIPTORS[detailKey(result)];
	const me = Authentication.currentUserId;
	const rankedGame =
		summary.ranked === true &&
		result.kind !== 'canceled' &&
		isTableGameRankedKind(summary.game) &&
		summary.players.some((player) => player.user_id === me)
			? summary.game
			: null;
	return (
		<output className={styles.banner} data-result={result.kind} data-flx="table-game.table-game-result.banner">
			<span className={styles.title} data-flx="table-game.table-game-result.title">
				{title}
			</span>
			{detail && (
				<span className={styles.detail} data-flx="table-game.table-game-result.detail">
					{i18n._(detail)}
				</span>
			)}
			{rankedGame && (
				<RankedChange
					channelId={channel.id}
					slot={summary.slot}
					game={rankedGame}
					data-flx="table-game.table-game-result.ranked-change"
				/>
			)}
		</output>
	);
});
